import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import globalVariables from "GlobalVariable"; // Assuming this holds your API domain and logo
import Spinner from "assets/components/spinner"; // Your spinner component
import { useAuth } from "context/AuthContext"; // Your AuthContext

// --- Helper Hook to get URL Query Parameters ---
function useQuery() {
    return new URLSearchParams(useLocation().search);
}

const SubscriptionSignup = () => {
    const query = useQuery();
    const navigate = useNavigate();
    const { login: contextLogin } = useAuth();

    // --- Main Logic States ---
    const [subscriptionId, setSubscriptionId] = useState(null);
    const [view, setView] = useState('signup'); // 'signup' or 'login'
    const [signupStep, setSignupStep] = useState('user'); // 'user' or 'business'

    // --- Data States ---
    const [newUserId, setNewUserId] = useState(null);

    // User Signup
    const [signupName, setSignupName] = useState("");
    const [signupLastName, setSignupLastName] = useState("");
    const [signupEmail, setSignupEmail] = useState("");
    const [signupPassword, setSignupPassword] = useState("");

    // User Login
    const [loginEmail, setLoginEmail] = useState("");
    const [loginPassword, setLoginPassword] = useState("");

    // Business Details
    const [businessForm, setBusinessForm] = useState({
        business_name: "",
        business_yelp_id: "",
        business_type: "",
        services: "",
        address: "",
        phone: "",
        website: "",
        timing: "",
        zip_code: "",
        manager: "",
        online_booking_link: "",
    });

    // --- UI/Feedback States ---
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");

    useEffect(() => {
        const subId = query.get("subscription_id");
        if (subId) {
            setSubscriptionId(subId);
        } else {
            setError("Subscription ID not found. This link is invalid or has expired.");
        }
    }, [query]);

    // --- Form Input Handlers ---
    const handleBusinessFormChange = (e) => {
        const { name, value } = e.target;
        setBusinessForm(prev => ({ ...prev, [name]: value }));
    };

    // --- API Call Handlers ---
    const handleUserSignupAndSubscriptionLink = async (e) => {
        e.preventDefault();
        setError("");
        setSuccessMessage("");
        setLoading(true);

        // console.log("checkpoint 1")
        try {
            // Step 1: Create User using the new endpoint
            const userPayload = {
                name: signupName,
                last_name: signupLastName,
                email: signupEmail,
                password: signupPassword,
            };
            const userResponse = await axios.post(`${globalVariables.domain}/user/create_user_external`, userPayload);
            // console.log("checkpoint 2")
            // console.log(userResponse.data, "User created successfully");
            if (!userResponse.data.success || !userResponse.data.content?._id) {
                throw new Error(userResponse.data.content || "Failed to create user account.");
            }

            const createdUserId = userResponse.data.content._id;

            setNewUserId(createdUserId); // Save new user ID
            // Step 2: Link Subscription using the retrieved token
            const patchPayload = { userId: createdUserId };
            await axios.patch(`${globalVariables.domain}/subscription/${subscriptionId}`, patchPayload, {

            });

            // Step 3: Transition to the business setup view
            setSuccessMessage("Account created successfully! Now, let's set up your business.");
            setSignupStep('business');

        } catch (err) {
            const errorMessage = err.response?.data?.content || err.message || "An unknown error occurred.";
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleBusinessSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccessMessage("");
        setLoading(true);

        const businessPayload = {
            ...businessForm,
            services: businessForm.services.split(',').map(s => s.trim()).filter(s => s),
            plan_id: subscriptionId,
            email: signupEmail, // Use the user's email for the business contact
        };

        try {
            // The auth token should be automatically included by your axios setup if it's configured with interceptors.
            // If not, you must retrieve it from context/state and add it to headers here as well.
            const businessResponse = await axios.post(
                `${globalVariables.domain}/business/create_business_and_data`,
                businessPayload
            );

            if (!businessResponse.data.success) {
                throw new Error(businessResponse.data.content || "Failed to create business profile.");
            }

            setSuccessMessage("Business setup complete! Redirecting to your dashboard...");

            await axios.patch(`${globalVariables.domain}/user/`, { approved: true, business_yelp_ids: [businessForm.business_yelp_id], _id: newUserId }, {

            });


            setTimeout(() => navigate("/dashboard_client"), 2000);

        } catch (err) {
            const errorMessage = err.response?.data?.content || err.message || "An unknown error occurred.";
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleLoginSubmit = async (e) => {
        e.preventDefault();
        // This login flow now simply logs in the user.
        // Linking subscriptions for existing users would need a separate, defined backend logic.
        setError("");
        setLoading(true);
        try {
            const response = await axios.post(`${globalVariables.domain}/user/login`, {
                email: loginEmail,
                password: loginPassword,
                external: true
            });
            if (response.data.success) {
                // setSuccessMessage("Login successful! Redirecting...");
                // contextLogin(response.data.content.json_web_token);
                // After login, you might want to check if they need to set up a business or link the subscription
                // This would require additional logic. For now, it redirects.
                // setTimeout(() => navigate("/dashboard_client"), 2000)


                const createdUserId = response.data.content.user_id;

                setNewUserId(createdUserId); // Save new user ID
                // Step 2: Link Subscription using the retrieved token
                const patchPayload = { userId: createdUserId };
                await axios.patch(`${globalVariables.domain}/subscription/${subscriptionId}`, patchPayload, {

                });

                // Step 3: Transition to the business setup view
                setSuccessMessage("Account login successfully! Now, let's set up your business.");
                setSignupStep('business');
            } else {
                setError(response.data.content || "Login failed.");
            }
        } catch (err) {
            setError(err.response?.data?.content || "Incorrect credentials or connection error.");
        } finally {
            setLoading(false);
        }
    };

    const commonInputClass = "w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500";
    const commonButtonClass = "w-full text-white font-bold rounded-full p-2.5 bg-orange-500 hover:bg-orange-600 transition duration-300 disabled:bg-orange-300";
    const checkboxLabelClass = "flex items-center space-x-3 text-sm font-medium text-gray-700 cursor-pointer";
    const checkboxClass = "form-checkbox h-4 w-4 text-orange-600 border-gray-400 rounded focus:ring-orange-500 focus:ring-offset-0";

    if (!subscriptionId && !error) { /* ... Loading state ... */ }
    if (error && !subscriptionId) { /* ... Invalid link state ... */ }

    const renderUserSignupForm = () => (
        <form onSubmit={handleUserSignupAndSubscriptionLink} className="flex flex-col gap-4 animate-fade-in">
            <h1 className="text-2xl md:text-3xl font-bold text-center text-gray-800 mb-2">Create Your Account</h1>
            <p className="text-sm text-gray-600 text-center mb-4">First, let's create your user account to link your subscription.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><input type="text" value={signupName} onChange={(e) => setSignupName(e.target.value)} required placeholder="Name" className={commonInputClass} /></div>
                <div><input type="text" value={signupLastName} onChange={(e) => setSignupLastName(e.target.value)} required placeholder="Last Name" className={commonInputClass} /></div>
            </div>
            <div><input type="email" value={signupEmail} onChange={(e) => setSignupEmail(e.target.value)} required placeholder="Email" className={commonInputClass} /></div>
            <div><input type="password" value={signupPassword} onChange={(e) => setSignupPassword(e.target.value)} required placeholder="Create Password" className={commonInputClass} /></div>
            <button type="submit" className={commonButtonClass} disabled={loading}>{loading ? <Spinner /> : "Create Account & Continue"}</button>
        </form>
    );

    const renderBusinessSetupForm = () => (
        <form onSubmit={handleBusinessSubmit} className="flex flex-col gap-4 animate-fade-in">
            <h1 className="text-2xl md:text-3xl font-bold text-center text-gray-800 mb-2">Set Up Your Business</h1>
            <p className="text-sm text-gray-600 text-center mb-4">Great! Your account is ready. Now, please provide your business details.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 pt-4">
                <div><label className="text-sm font-medium text-gray-600">Business Name*</label><input name="business_name" type="text" value={businessForm.business_name} onChange={handleBusinessFormChange} className={commonInputClass} required /></div>
                <div><label className="text-sm font-medium text-gray-600">Business Yelp ID (optional)</label><input name="business_yelp_id" type="text" value={businessForm.business_yelp_id} onChange={handleBusinessFormChange} placeholder="" className={commonInputClass} /></div>
                <div><label className="text-sm font-medium text-gray-600">Business Type*</label><input name="business_type" type="text" value={businessForm.business_type} onChange={handleBusinessFormChange} placeholder="" className={commonInputClass} required /></div>
                <div><label className="text-sm font-medium text-gray-600">Phone</label><input name="phone" type="tel" value={businessForm.phone} onChange={handleBusinessFormChange} placeholder="+14085554444" className={commonInputClass} /></div>
                <div className="md:col-span-2"><label className="text-sm font-medium text-gray-600">Address</label><input name="address" type="text" value={businessForm.address} onChange={handleBusinessFormChange} placeholder="" className={commonInputClass} /></div>
                <div className="md:col-span-2"><label className="text-sm font-medium text-gray-600">Services (comma-separated)</label><input name="services" type="text" value={businessForm.services} onChange={handleBusinessFormChange} placeholder="" className={commonInputClass} /></div>
                <div className="md:col-span-2"><label className="text-sm font-medium text-gray-600">Website</label><input name="website" type="url" value={businessForm.website} onChange={handleBusinessFormChange} placeholder="" className={commonInputClass} /></div>
                <div className="md:col-span-2"><label className="text-sm font-medium text-gray-600">Online Booking Link</label><input name="online_booking_link" type="url" value={businessForm.online_booking_link} onChange={handleBusinessFormChange} placeholder="" className={commonInputClass} /></div>
                <div><label className="text-sm font-medium text-gray-600">Manager Name</label><input name="manager" type="text" value={businessForm.manager} onChange={handleBusinessFormChange} placeholder="" className={commonInputClass} /></div>
                <div><label className="text-sm font-medium text-gray-600">Opening Hours</label><input name="timing" type="text" value={businessForm.timing} onChange={handleBusinessFormChange} placeholder="e.g., Mon-Fri: 9am - 6pm" className={commonInputClass} /></div>
            </div>
            <button type="submit" className={`${commonButtonClass} mt-4`} disabled={loading}>{loading ? <Spinner /> : "Save Business & Go to Dashboard"}</button>
        </form>
    );

    return (
        <div className="flex min-h-screen flex-col justify-center items-center bg-gradient-to-r from-red-50 to-cyan-50 p-4">
            <div className="mb-8"><img src="https://samedaypet.s3.amazonaws.com/ba4faeb5-cc14-4e69-8846-204001c9f41f.png" alt="Logo" className="max-w-xs w-full h-auto" /></div>
            <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-2xl">
                <div className="mb-6 pb-6 border-b border-gray-200">
                    <label className={checkboxLabelClass}>
                        <input type="checkbox" checked={view === 'login'} onChange={(e) => setView(e.target.checked ? 'login' : 'signup')} className={checkboxClass} />
                        <span>I already have an account with Azucar AI.</span>
                    </label>
                </div>

                {view === 'login' && (
                    <>

                        {signupStep === 'user' && (

                            <div className="max-w-md mx-auto">
                                <form onSubmit={handleLoginSubmit} className="flex flex-col gap-4 animate-fade-in">
                                    <h1 className="text-2xl md:text-3xl font-bold text-center text-gray-800 mb-4">Sign In</h1>
                                    <p className="text-sm text-gray-600 text-center mb-4">Sign in to your account.</p>
                                    <div><input type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required placeholder="Email" className={commonInputClass} /></div>
                                    <div><input type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} required placeholder="Password" className={commonInputClass} /></div>
                                    <button type="submit" className={commonButtonClass} disabled={loading}>{loading ? <Spinner /> : "Sign In"}</button>
                                </form>
                            </div>
                        )}

                        {signupStep === 'business' && (
                            renderBusinessSetupForm()
                        )}
                    </>
                )}

                {view === 'signup' && (
                    signupStep === 'user' ? renderUserSignupForm() : renderBusinessSetupForm()
                )}

                {error && <p className="mt-4 text-red-500 text-sm text-center bg-red-50 p-3 rounded-lg">{error}</p>}
                {successMessage && <p className="mt-4 text-green-600 text-sm text-center bg-green-50 p-3 rounded-lg">{successMessage}</p>}
            </div>
        </div>
    );
};

export default SubscriptionSignup;
